import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FormModal } from '@/components/ui/form-modal';
import { Button } from '@/components/ui/button';
import { MultiIdCombobox } from '@/components/ui/multi-id-combobox';
import config from 'constants/apiConfig';
import { changeGroupAdminNameToOrgAdmin } from 'components/Roles/RolesUtils/groupToOrgTransformations';
import type { InternalLayer, LayerGrant } from 'hooks/internalLayer/types';
import {
  useLayerGrantsQuery,
  useReplaceLayerGrantsMutation,
} from 'hooks/internalLayer/useLayerGrants';

const ONLY_ME = '__only_me__';

function withoutOnlyMe(ids: string[]) {
  return ids.filter((id) => id !== ONLY_ME);
}

/** Picking Only me clears roles. Picking a role clears Only me. */
function exclusiveSelection(next: string[]) {
  if (next.length === 0) return [ONLY_ME];
  const addedOnlyMe = next[next.length - 1] === ONLY_ME;
  if (addedOnlyMe) return [ONLY_ME];
  const roles = withoutOnlyMe(next);
  return roles.length > 0 ? roles : [ONLY_ME];
}

function grantOptionValue(grant: LayerGrant) {
  if (
    (grant.scopeType === 'group' || grant.scopeType === 'aor') &&
    grant.scopeId
  ) {
    return `${grant.subjectId}|${grant.scopeType}|${grant.scopeId}`;
  }
  return grant.subjectId;
}

function selectionFromGrants(grants: LayerGrant[], includeScoped: boolean) {
  const roles = grants.filter(
    (grant) =>
      grant.subjectType === 'role' && (includeScoped || !grant.scopeType)
  );
  if (roles.length === 0) {
    return { viewIds: [ONLY_ME], editIds: [ONLY_ME] };
  }
  const viewIds = roles
    .filter((grant) => grant.canView || grant.canEdit)
    .map(grantOptionValue);
  const editIds = roles.filter((grant) => grant.canEdit).map(grantOptionValue);
  return {
    viewIds: viewIds.length > 0 ? viewIds : [ONLY_ME],
    editIds: editIds.length > 0 ? editIds : [ONLY_ME],
  };
}

function grantsFromSelection(
  viewIds: string[],
  editIds: string[]
): LayerGrant[] {
  if (viewIds.includes(ONLY_ME)) return [];
  return withoutOnlyMe(viewIds).flatMap((value) => {
    const [subjectId, scopeType, scopeId] = value.split('|');
    if (
      (scopeType === 'group' || scopeType === 'aor') &&
      subjectId &&
      scopeId
    ) {
      return [
        {
          subjectType: 'role' as const,
          subjectId,
          canView: true,
          canEdit: editIds.includes(value),
          scopeType,
          scopeId,
        },
      ];
    }
    return [
      {
        subjectType: 'role' as const,
        subjectId: value,
        canView: true,
        canEdit: editIds.includes(value),
      },
    ];
  });
}

function PermissionFields({
  options,
  initialViewIds,
  initialEditIds,
  pending,
  onCancel,
  onConfirm,
}: {
  options: { value: string; label: string }[];
  initialViewIds: string[];
  initialEditIds: string[];
  pending: boolean;
  onCancel: () => void;
  onConfirm: (grants: LayerGrant[]) => void;
}) {
  const [viewIds, setViewIds] = useState(initialViewIds);
  const [editIds, setEditIds] = useState(initialEditIds);

  const setView = (next: string[]) => {
    const view = exclusiveSelection(next);
    setViewIds(view);
    if (view.includes(ONLY_ME)) {
      setEditIds([ONLY_ME]);
      return;
    }
    setEditIds((current) => {
      if (current.includes(ONLY_ME)) return [ONLY_ME];
      const kept = current.filter((id) => view.includes(id));
      return kept.length > 0 ? kept : [ONLY_ME];
    });
  };

  const setEdit = (next: string[]) => {
    const edit = exclusiveSelection(next);
    if (edit.includes(ONLY_ME)) {
      setEditIds([ONLY_ME]);
      return;
    }
    setEditIds(edit);
    setViewIds((current) => {
      if (current.includes(ONLY_ME)) return edit;
      return Array.from(new Set([...current, ...edit]));
    });
  };

  const editOptions = viewIds.includes(ONLY_ME)
    ? options.filter((option) => option.value === ONLY_ME)
    : options.filter(
        (option) => option.value === ONLY_ME || viewIds.includes(option.value)
      );

  return (
    <>
      <div className="tw:flex tw:flex-col tw:gap-4">
        <div className="tw:flex tw:flex-col tw:gap-1.5">
          <div className="tw:text-sm">View layer</div>
          <MultiIdCombobox
            options={options}
            value={viewIds}
            onChange={setView}
            placeholder="Search roles"
            disabled={pending}
          />
        </div>
        <div className="tw:flex tw:flex-col tw:gap-1.5">
          <div className="tw:text-sm">Edit layer</div>
          <MultiIdCombobox
            options={editOptions}
            value={editIds}
            onChange={setEdit}
            placeholder="Search roles"
            disabled={pending}
          />
        </div>
      </div>
      <div className="tw:sticky tw:bottom-0 tw:flex tw:justify-end tw:gap-2 tw:bg-background tw:pt-3">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={pending}
        >
          Cancel
        </Button>
        <Button
          type="button"
          onClick={() => onConfirm(grantsFromSelection(viewIds, editIds))}
          disabled={pending}
        >
          {pending ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </>
  );
}

export function LayerPermissionsDialog({
  layer,
  open,
  onClose,
}: {
  layer: InternalLayer;
  open: boolean;
  onClose: () => void;
}) {
  const inIncident = Boolean(layer.incidentId);
  const organizationRolesUnlocked = Boolean(
    layer.incidentId && layer.approvedAt
  );
  const replaceGrants = useReplaceLayerGrantsMutation();
  const grantsQuery = useLayerGrantsQuery(layer.id, open);
  const { data: roles = [], isFetched: rolesFetched } = useQuery({
    queryKey: [
      'layer-draft-roles',
      layer.groupGuid,
      inIncident ? 'incident' : 'hub',
      layer.incidentId ?? null,
    ],
    enabled: open && Boolean(layer.groupGuid),
    queryFn: async () => {
      const response = await axios.post<{ id: string; name: string }[]>(
        config.apiGateway.fetchRolesForLayerDraft,
        {
          // eslint-disable-next-line camelcase -- matches the roles API body
          group_guid: layer.groupGuid,
          // eslint-disable-next-line camelcase -- matches the roles API body
          incident_id: layer.incidentId ?? null,
        }
      );
      return Array.isArray(response.data) ? response.data : [];
    },
  });
  const { data: organizationRoles = [], isFetched: organizationRolesFetched } =
    useQuery({
      queryKey: ['layer-publish-roles', layer.groupGuid],
      enabled: open && organizationRolesUnlocked && Boolean(layer.groupGuid),
      queryFn: async () => {
        const response = await axios.post<
          {
            roleId: string;
            roleName: string;
            scopeType: 'group' | 'aor';
            scopeId: string;
            scopeLabel: string;
          }[]
        >(config.apiGateway.fetchRolesForLayerPublish, {
          // eslint-disable-next-line camelcase -- matches the roles API body
          group_guid: layer.groupGuid,
        });
        return Array.isArray(response.data) ? response.data : [];
      },
    });

  const roleOptions = roles.map((role) => ({
    value: String(role.id),
    label: inIncident ? role.name : changeGroupAdminNameToOrgAdmin(role.name),
  }));
  const organizationOptions = organizationRoles.map((role) => ({
    value: `${role.roleId}|${role.scopeType}|${role.scopeId}`,
    label: `${changeGroupAdminNameToOrgAdmin(role.roleName)} · ${role.scopeLabel}`,
  }));
  const options = [
    { value: ONLY_ME, label: 'Only me' },
    ...roleOptions,
    ...(organizationRolesUnlocked ? organizationOptions : []),
  ];
  const initial = selectionFromGrants(
    grantsQuery.data ?? [],
    organizationRolesUnlocked
  );
  const ready =
    grantsQuery.isSuccess &&
    rolesFetched &&
    (!organizationRolesUnlocked || organizationRolesFetched);

  const save = async (grants: LayerGrant[]) => {
    await replaceGrants.mutateAsync({
      internalLayerId: layer.id,
      grants,
      quiet: true,
    });
    toast.success('Permissions updated.');
    onClose();
  };

  const description = organizationRolesUnlocked
    ? 'Choose which incident roles can view and edit this layer, and which organization or area of responsibility roles can view and edit it outside the workspace. Only me keeps the layer with you.'
    : inIncident
      ? 'Choose which incident roles can view this layer and which can edit it. Only me keeps the layer with you.'
      : 'Choose which organization and area of responsibility roles can view this layer and which can edit it. Only me keeps the layer with you.';

  return (
    <FormModal
      open={open}
      onOpenChange={(next) => {
        if (!next && !replaceGrants.isPending) onClose();
      }}
      title="Permissions"
      description={description}
    >
      {grantsQuery.isError ? (
        <p className="tw:mb-0 tw:text-sm tw:text-muted-foreground">
          Permissions can be changed by the person who created this layer.
        </p>
      ) : !ready ? (
        <p className="tw:mb-0 tw:text-sm tw:text-muted-foreground">
          Loading permissions…
        </p>
      ) : (
        <PermissionFields
          key={`${layer.id}:${initial.viewIds.join('|')}:${initial.editIds.join('|')}:${options
            .map((option) => option.value)
            .join('|')}`}
          options={options}
          initialViewIds={initial.viewIds}
          initialEditIds={initial.editIds}
          pending={replaceGrants.isPending}
          onCancel={onClose}
          onConfirm={(grants) => void save(grants)}
        />
      )}
    </FormModal>
  );
}
